import { CSSProperties } from 'react';
import useWidthBreakpoint from '../../../../../hooks/useWidthBreakpoint';
import { useResultData } from '../context';
import { profitColor, tokens } from '../tokens';
import { pick } from '../responsive';
import EditorialHeader from './shared/EditorialHeader';

const Hero = () => {
  const displayData = useResultData();
  const { breakpoint } = useWidthBreakpoint();

  // vs Buy & Hold is a multiplier (×N,NN): ≥1 beat the market (green), <1 lagged (red).
  const vsBuyAndHoldColor = (() => {
    const value = parseFloat(
      displayData.vsBuyAndHold.replace('×', '').replace(',', '.'),
    );
    return Number.isFinite(value) && value < 1 ? tokens.red : tokens.green;
  })();

  const padding = pick(breakpoint, {
    sm: '0 0 4px',
    md: '0 0 4px',
    lg: '0 0 4px',
  });
  const heroNumber = pick(breakpoint, { sm: 64, md: 80, lg: 96 });
  const rankNumber = pick(breakpoint, { sm: 44, md: 52, lg: 60 });
  const stripValueScale = pick(breakpoint, { sm: 0.8, md: 0.9, lg: 1 });

  const wrapperGrid: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: { display: 'flex', flexDirection: 'column', gap: 0 },
    md: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gridTemplateRows: 'repeat(2, auto)',
      gap: 0,
    },
    lg: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gridTemplateRows: 'repeat(2, auto)',
      gap: 0,
    },
  });

  const rank1Cell: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: { padding: '0px 4px 28px' },
    md: { gridColumn: '1 / 3', gridRow: '1', padding: '0px 28px 32px' },
    lg: {
      gridColumn: '1 / 3',
      gridRow: '1 / 3',
      padding: '0px 32px 36px',
      borderRight: tokens.borderFaint,
    },
  });

  const rank2Cell: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: { padding: '0px 4px' },
    md: { padding: '0px 24px', borderRight: tokens.borderFaint },
    lg: { padding: '0px 28px', borderRight: tokens.borderFaint },
  });

  const rank3Cell: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: { padding: '0px 4px' },
    md: { padding: '0px 24px' },
    lg: { padding: '0px 28px' },
  });

  const stripGrid: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' },
    md: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gridColumn: '1 / 3',
      gridRow: '2',
    },
    lg: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gridColumn: '3 / 5',
      gridRow: '2',
    },
  });

  const stripItems: Array<[string, string, string, number]> = [
    ['BALANCE', displayData.balanceArrow, tokens.textPrimary, 22],
    ['FEES', displayData.totalFees, tokens.textPrimary, 26],
    ['DURATION', displayData.durationShort, tokens.textPrimary, 26],
    ['TXNS', `${displayData.txCount}`, tokens.textPrimary, 30],
    ['WIN', displayData.winRate, tokens.textPrimary, 30],
  ];

  return (
    <div
      style={{
        background: tokens.backgroundBase,
        color: tokens.textPrimary,
        fontFamily: tokens.fontSans,
        padding,
        maxWidth: 1400,
        margin: '0 auto',
      }}
    >
      <EditorialHeader number="02" label="The Gist" />

      <div style={wrapperGrid}>
        {/* Rank 1 — Stable Profit */}
        <div style={rank1Cell}>
          <div
            style={{
              fontSize: 15,
              color: tokens.textTertiary,
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
              marginBottom: 10,
            }}
          >
            Stable Profit
          </div>
          <div
            style={{
              fontSize: heroNumber,
              fontFamily: tokens.fontDisplay,
              color: displayData.subscriptionView
                ? profitColor(displayData.stableProfit)
                : tokens.green,
              lineHeight: 0.88,
              letterSpacing: '-0.03em',
              wordBreak: 'break-word',
            }}
          >
            {displayData.stableProfit}
          </div>
          <div
            style={{
              fontSize: 15,
              color: tokens.textMuted,
              marginTop: 20,
              lineHeight: 1.55,
            }}
          >
            {displayData.startBalance} compounded into{' '}
            <b
              style={{
                color: displayData.subscriptionView
                  ? profitColor(displayData.stableProfit)
                  : tokens.green,
              }}
            >
              {displayData.endBalance}
            </b>{' '}
            across {displayData.txCount} transactions.
          </div>
          <div
            style={{
              fontSize: 15,
              color: tokens.textMuted,
              marginTop: 8,
              lineHeight: 1.55,
            }}
          >
            {displayData.startBalanceAsset} compounded into{' '}
            <b style={{ color: profitColor(displayData.assetProfit) }}>
              {displayData.endBalanceAsset}
            </b>{' '}
            across {displayData.txCount} transactions.
          </div>
        </div>

        {/* Rank 2 — vs Buy & Hold */}
        <div style={rank2Cell}>
          <div
            style={{
              fontSize: 12,
              color: tokens.textTertiary,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
            }}
          >
            vs Buy &amp; Hold
          </div>
          <div
            style={{
              fontSize: rankNumber,
              fontFamily: tokens.fontDisplay,
              color: displayData.subscriptionView
                ? vsBuyAndHoldColor
                : tokens.green,
              lineHeight: 0.95,
              marginTop: 8,
            }}
          >
            {displayData.vsBuyAndHold}
          </div>
        </div>

        {/* Rank 3 — Score (falls back to Asset Profit) */}
        <div style={rank3Cell}>
          <div
            style={{
              fontSize: 12,
              color: tokens.textTertiary,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
            }}
          >
            {displayData.score !== null ? 'Score' : 'Asset Profit'}
          </div>
          <div
            style={{
              fontSize: rankNumber,
              fontFamily: tokens.fontDisplay,
              color: displayData.subscriptionView
                ? profitColor(
                    displayData.score !== null
                      ? displayData.score
                      : displayData.assetProfit,
                  )
                : tokens.green,
              lineHeight: 0.95,
              marginTop: 8,
            }}
          >
            {displayData.score !== null
              ? displayData.score
              : displayData.assetProfit}
          </div>
        </div>

        {/* Strip — BALANCE / FEES / DURATION / TXNS / WIN */}
        <div style={stripGrid}>
          {stripItems.map(([label, value, color, fontSize], index) => (
            <div
              key={label}
              style={{
                padding: '20px 16px',
                borderTop: tokens.borderFaint,
                borderRight:
                  index < stripItems.length - 1 ? tokens.borderFaint : 'none',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: tokens.textTertiary,
                  fontFamily: tokens.fontMono,
                  letterSpacing: '0.12em',
                  fontWeight: 700,
                }}
              >
                {label}
              </div>
              <div
                style={{
                  fontSize: fontSize * stripValueScale,
                  fontFamily: tokens.fontDisplay,
                  color,
                  lineHeight: 1.1,
                  marginTop: 6,
                }}
              >
                {value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Hero;
